import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface CatalogFilters {
  page: number;
  search: string;
  categoryIds: number[];
  colorId: number | undefined;
  variationValue: string | undefined;
  gender: number | undefined;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  selectedSortIndex: number;

  searchInput: string;
  minPriceInput: string;
  maxPriceInput: string;

  setPage: (page: number) => void;
  handleSearchChange: (value: string) => void;
  handleCategoryChange: (ids: number[]) => void;
  handleColorChange: (id: number | undefined) => void;
  handleVariationChange: (value: string | undefined) => void;
  handleGenderChange: (value: number | undefined) => void;
  handleMinPriceChange: (raw: string) => void;
  handleMaxPriceChange: (raw: string) => void;
  handleSortChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  clearFilters: () => void;

  hasFilters: boolean;
}

export function useCatalogFilters(): CatalogFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const search = searchParams.get('q') ?? '';
  const categoryIds = searchParams.get('cat')
    ? searchParams.get('cat')!.split(',').map(Number).filter((n) => !isNaN(n))
    : [];
  const colorId = searchParams.get('color') !== null ? Number(searchParams.get('color')) : undefined;
  const variationValue = searchParams.get('var') ?? undefined;
  const gender = searchParams.get('gender') !== null ? Number(searchParams.get('gender')) : undefined;
  const minPrice = searchParams.get('minP') !== null ? Number(searchParams.get('minP')) : undefined;
  const maxPrice = searchParams.get('maxP') !== null ? Number(searchParams.get('maxP')) : undefined;
  const selectedSortIndex = parseInt(searchParams.get('sort') ?? '0', 10) || 0;

  const [searchInput, setSearchInput] = useState(search);
  const [minPriceInput, setMinPriceInput] = useState(minPrice !== undefined ? String(minPrice) : '');
  const [maxPriceInput, setMaxPriceInput] = useState(maxPrice !== undefined ? String(maxPrice) : '');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const debounce = (fn: () => void) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fn, 400);
  };

  const buildParams = (overrides: Record<string, string | undefined>): URLSearchParams => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(overrides)) {
      if (value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    return next;
  };

  const setPage = (newPage: number) => {
    const next = buildParams({ page: newPage === 1 ? undefined : String(newPage) });
    setSearchParams(next);
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debounce(() => {
      const next = buildParams({ q: value || undefined, page: undefined });
      setSearchParams(next, { replace: true });
    });
  };

  const handleCategoryChange = (ids: number[]) => {
    const next = buildParams({
      cat: ids.length > 0 ? ids.join(',') : undefined,
      page: undefined,
    });
    setSearchParams(next, { replace: true });
  };

  const handleColorChange = (id: number | undefined) => {
    const next = buildParams({ color: id !== undefined ? String(id) : undefined, page: undefined });
    setSearchParams(next, { replace: true });
  };

  const handleVariationChange = (value: string | undefined) => {
    const next = buildParams({ var: value ?? undefined, page: undefined });
    setSearchParams(next, { replace: true });
  };

  const handleGenderChange = (value: number | undefined) => {
    const next = buildParams({ gender: value !== undefined ? String(value) : undefined, page: undefined });
    setSearchParams(next, { replace: true });
  };

  const handleMinPriceChange = (raw: string) => {
    setMinPriceInput(raw);
    const parsed = raw === '' ? undefined : Number(raw);
    const value = parsed !== undefined && !isNaN(parsed) ? String(parsed) : undefined;
    debounce(() => {
      const next = buildParams({ minP: value, page: undefined });
      setSearchParams(next, { replace: true });
    });
  };

  const handleMaxPriceChange = (raw: string) => {
    setMaxPriceInput(raw);
    const parsed = raw === '' ? undefined : Number(raw);
    const value = parsed !== undefined && !isNaN(parsed) ? String(parsed) : undefined;
    debounce(() => {
      const next = buildParams({ maxP: value, page: undefined });
      setSearchParams(next, { replace: true });
    });
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const idx = Number(e.target.value);
    const next = buildParams({ sort: idx === 0 ? undefined : String(idx), page: undefined });
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    setSearchInput('');
    setMinPriceInput('');
    setMaxPriceInput('');
    setSearchParams({});
  };

  const hasFilters = searchParams.size > 0;

  return {
    page,
    search,
    categoryIds,
    colorId,
    variationValue,
    gender,
    minPrice,
    maxPrice,
    selectedSortIndex,

    searchInput,
    minPriceInput,
    maxPriceInput,

    setPage,
    handleSearchChange,
    handleCategoryChange,
    handleColorChange,
    handleVariationChange,
    handleGenderChange,
    handleMinPriceChange,
    handleMaxPriceChange,
    handleSortChange,
    clearFilters,

    hasFilters,
  };
}
